import { useMutation } from '@apollo/react-hooks'
import Button from '@berlitz/button'
import PortalAvailabilityCalendar from '@classes/AvailabilityCalendar'
import WarningModal from '@components/Modal/WarningModal'
import Radio from '@components/Radio'
import { PUT_INSTRUCTOR_AVAILABILITY } from '@mutations/instructorAvailability'
import { PUT_INSTRUCTOR_UNAVAILABILITY } from '@mutations/instructorUnAvailability'
import React, { useState } from 'react'
import { useEffect } from 'react'
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { IAvailabilityResponse, IUnAvailabilityResponse, TEventResource } from './interface'
import { ButtonWrapper, RadioWrapper } from './style'

interface IPDeleteModal {
  open: boolean
  onClose: () => void
  onDeleting: () => void
  onDeleted: (success: boolean) => void
  resource: TEventResource
  AvailabilityCalendar: PortalAvailabilityCalendar
}

const DeleteModal: React.FC<IPDeleteModal & InjectedIntlProps> = ({
  open,
  intl,
  resource,
  onClose,
  onDeleting,
  onDeleted,
  AvailabilityCalendar,
}) => {
  const [isRecurring, setIsRecurring] = useState(false)
  const onCompleted = (data: IAvailabilityResponse & IUnAvailabilityResponse) =>
    onDeleted((data?.putInstructorAvailability || data?.putInstructorUnAvailability)?.ServiceStatus?.Success || false)
  const onError = () => onDeleted(false)

  const [deleteAvailability] = useMutation<IAvailabilityResponse>(PUT_INSTRUCTOR_AVAILABILITY, {
    onCompleted,
    onError,
  })
  const [deleteUnAvailability] = useMutation<IUnAvailabilityResponse>(PUT_INSTRUCTOR_UNAVAILABILITY, {
    onCompleted,
    onError,
  })

  useEffect(() => {
    if (open) {
      setIsRecurring(false)
    }
  }, [open])

  const onDeleteEvent = (CancelSessionFlag: boolean, resource?: TEventResource) => {
    try {
      const payload = resource?.getDeletePayload(CancelSessionFlag)
      if (resource?.IsAvailability) {
        deleteAvailability({
          variables: {
            Availabilities: [payload],
          },
        })
      }

      if (resource?.IsUnAvailability && resource) {
        const Unavailabilities = AvailabilityCalendar.getTimeOffDeletePayload(resource, isRecurring)
        deleteUnAvailability({
          variables: {
            Unavailabilities,
          },
        })
      }
      onDeleting()
    } catch (error) {
      onDeleted(false)
    }
  }

  const handleCloseModal = () => {
    onClose()
  }

  return (
    <>
      <WarningModal
        open={open}
        onClose={() => {
          handleCloseModal()
        }}
        proceedLabel={intl.formatMessage({ id: 'Delete' })}
        onProceed={() => onDeleteEvent(true, resource)}
        title={intl.formatMessage({ id: `Delete ${resource?.IsUnAvailability ? 'time off' : 'availability'}` })}
        subtitle={
          resource?.IsUnAvailability ? (
            <RadioWrapper>
              <div>
                {intl.formatMessage({
                  id: 'Are you sure you want to continue?',
                })}
              </div>
              <div>
                {intl.formatMessage({
                  id: 'This action cannot be undone.',
                })}
              </div>
            </RadioWrapper>
          ) : (
            intl.formatMessage({
              id: 'You may have lessons scheduled in this timeframe. What would you like to do with these lessons?',
            })
          )
        }
        customButtons={
          resource?.IsUnAvailability ? (
            <ButtonWrapper>
              <Button onClick={handleCloseModal}>
                <FormattedMessage id="Cancel" />
              </Button>
              <Button buttonType="error" onClick={() => onDeleteEvent(false, resource)}>
                <FormattedMessage id="Delete" />
              </Button>
            </ButtonWrapper>
          ) : (
            <>
              <Button
                onClick={() => {
                  handleCloseModal()
                }}
              >
                <FormattedMessage id="Exit" />
              </Button>
              <Button onClick={() => onDeleteEvent(true, resource)}>
                <FormattedMessage id="Cancel lessons" />
              </Button>
              <Button onClick={() => onDeleteEvent(false, resource)}>
                <FormattedMessage id="Keep lessons" />
              </Button>
            </>
          )
        }
      />
    </>
  )
}

export default injectIntl(DeleteModal)
